import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { AppState } from '../store';
import { selectUserPermissions } from '../store/selectors';
import { FeatureFlagService } from '../shared/services/feature-flag.service';
import { checkIfPlatformSite } from '../common/platform-utils';

const DESIGN_STORAGE_KEY = 'och:headerFooterDesign';

/**
 * Rollout switch for the header/footer redesign (mirrors course-page-switcher):
 * gated behind the headerFooterRedesign flag ("QA-TEST" = platform admins), with
 * a localStorage-persisted toggle. The platform site always stays classic.
 */
@Injectable({ providedIn: 'root' })
export class RevampDesignService {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly featureFlagService = inject(FeatureFlagService);

  private readonly isPlatformSite = checkIfPlatformSite();

  // permissions bridge so OnPush consumers re-evaluate the flag once they load
  private readonly permissionsSig = toSignal(this.store.pipe(select(selectUserPermissions)), { initialValue: undefined });

  private readonly storedPreference = signal<'new' | 'classic' | null>(this.readStoredPreference());

  readonly showToggle = computed(() => {
    this.permissionsSig();
    return !this.isPlatformSite && !!this.featureFlagService.features?.['headerFooterRedesign'];
  });

  readonly useNewDesign = computed(() => {
    if (!this.showToggle()) {
      return false;
    }
    const stored = this.storedPreference();
    return stored ? stored === 'new' : true;
  });

  toggleDesign(): void {
    const next = this.useNewDesign() ? 'classic' : 'new';
    this.storedPreference.set(next);
    try {
      localStorage.setItem(DESIGN_STORAGE_KEY, next);
    } catch {
      // storage unavailable (private mode) — toggle still works for the session
    }
  }

  private readStoredPreference(): 'new' | 'classic' | null {
    try {
      const stored = localStorage.getItem(DESIGN_STORAGE_KEY);
      return stored === 'new' || stored === 'classic' ? stored : null;
    } catch {
      return null;
    }
  }
}
